import { FormEventHandler, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { EMAIL_NOT_CONFIRMED_CODE } from 'shared/email-not-confirmed'
import type { JwtTokenPayload } from 'shared/jwt'
import { buttonVariants } from 'ui/button'
import { Input } from 'ui/input'
import { LoadingButton } from 'ui/loading-button'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { PasswordlessLogin } from '~/boundedContext/webauthn/components/passwordless-login'
import { GoogleIcon } from '~/components/google-icon'
import { LinkButton } from '~/components/link-button'
import { OrDelimiter } from '~/components/or-delimiter'
import { LS_JWT_KEY } from '~/const/api'
import { useSearchParamsError } from '~/hooks/use-search-params-error'
import { useAccountStore } from '~/stores/account'
import { useSettingsStore } from '~/stores/settings'
import { decodeJwt } from '~/utils/decode-jwt'
import { getPostAuthRedirectRoute } from '~/utils/get-post-auth-redirect-route'

import { AuthForm } from '../components/auth-form'

export const Login = () => {
  const emailRef = useRef<HTMLInputElement | null>(null)
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const isResendingConfirmationRef = useRef(false)
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const setAccount = useAccountStore(state => state.patchAccount)
  const setLanguagesIfEmpty = useSettingsStore(state => state.setLanguagesIfEmpty)

  const { t } = useTranslation()

  useSearchParamsError()

  const resendEmailConfirmation = (email: string) => {
    if (isResendingConfirmationRef.current) return

    isResendingConfirmationRef.current = true

    void apiClient
      .POST('/auth/resend-email-confirmation', {
        body: { email },
        params: { header: { origin: window.location.origin } },
      })
      .then(({ response }) => {
        if (response.status >= 400) return toast.error(t('Something went wrong'))

        toast.success(t('Confirmation email sent'))
      })
      .catch(error => {
        toast.error(t('Network error'))
        console.error({ message: 'Network error', error })
      })
      .finally(() => {
        isResendingConfirmationRef.current = false
      })
  }

  const onSubmit: FormEventHandler = e => {
    e.preventDefault()
    if (isLoading) return

    const email = emailRef.current?.value
    const password = passwordRef.current?.value
    if (!email || !password) return

    setIsLoading(true)
    void apiClient
      .POST('/auth/login', {
        body: { email, password },
      })
      .then(async ({ data: { jwtToken, user } = {}, response, error }) => {
        if (response.status === 403 && error?.message === EMAIL_NOT_CONFIRMED_CODE)
          return toast.error(t('Please confirm your email'), {
            action: {
              label: t('Resend'),
              onClick: () => resendEmailConfirmation(email),
            },
          })

        if (response.status === 401) return toast.error(t('Wrong credentials'))

        if (!jwtToken || !user || response.status >= 400) return toast.error(t('Something went wrong'))

        localStorage.setItem(LS_JWT_KEY, jwtToken)

        setAccount({
          ...user,
          lastSelectedLanguages: user.lastSelectedLanguages as [string, string],
          username: user.username ?? null,
          coins: user.coins ?? 0,
        })

        setLanguagesIfEmpty(user.lastSelectedLanguages as [string, string])

        const jwtPayload = decodeJwt<JwtTokenPayload>(jwtToken)

        return navigate({ to: getPostAuthRedirectRoute(jwtPayload?.subscriptionStatus) })
      })
      .catch(requestError => {
        toast.error(t('Network error'))
        console.error({ message: 'Network error', error: requestError })
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <AuthForm onSubmit={onSubmit}>
      <Input ref={emailRef} autoComplete="email webauthn" name="email" placeholder={t('email')} type="email" required />
      <Input
        ref={passwordRef}
        autoComplete="current-password webauthn"
        name="password"
        placeholder={t('password')}
        type="password"
        required
      />
      <Link className="text-end text-accent-foreground underline" to="/auth/forgot">
        {t('Forgot password')}
      </Link>
      <LoadingButton isLoading={isLoading} type="submit">
        {t('Login')}
      </LoadingButton>
      <PasswordlessLogin />
      <OrDelimiter />
      <a className={buttonVariants({ className: 'flex gap-2' })} href={`${import.meta.env.VITE_API_BASE}/auth/google`}>
        <GoogleIcon />
        {t('Login with google')}
      </a>
      <p className="text-center text-sm text-slate-700 dark:text-slate-200">
        <Trans
          components={{
            privacyLink: (
              <Link
                key="privacy-policy"
                className="whitespace-nowrap text-base font-bold underline"
                to="/privacy-policy"
              />
            ),
          }}
          i18nKey="By continuing, you agree to Lingo Cards Privacy Policy"
        />
      </p>
      <div className="mt-10 flex justify-center gap-2">
        <p>{t('Don t have an account')}</p>
        <Link className="text-accent-foreground underline" to="/auth/register">
          {t('Sign up')}
        </Link>
      </div>
      <LinkButton className="w-full" to="/demo/list" variant="outline">
        {t('Try demo')}
      </LinkButton>
    </AuthForm>
  )
}
